import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Select, SelectItem, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextInput } from '@carbon/react';
import { Download, Filter } from '@carbon/icons-react';
import { vnDayKey, timeZoneLabel } from '@ruby-cms/shared';
import { api } from '../api/client';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState, InlineError } from '../components/states';
import { Money, Pct } from '../components/format';
import { BarChart } from '../components/BarChart';
import { GRAIN_LABEL, GrainSwitch, PARTIAL_NOTE, PERIOD_WORD, periodDefaultFrom, periodLabel, periodTick, type Grain } from '../components/Analytics';
import { useAuth } from '../auth/AuthContext';

type Row = { bet: string; win: string; refund: string; admin: string; ggr: string; players: number; rows: number };
type DayRow = Row & { day: string; partial?: boolean };
type GameRow = Row & { game: string; category: string | null; rtp: number | null; rtpTheory: number | null; rtpLow: number | null; rtpHigh: number | null };
type TieRow = { day: string; ties: number; stake: string; returned: string; kept: string; partial: boolean };
type TieGame = { game: string; label: string; ties: number; stake: string; returned: string; kept: string };
type TieData = { rows: TieRow[]; games: TieGame[]; totals: { ties: number; stake: string; returned: string; kept: string }; unmatched: number; notCovered: string[] };
type PlayerRow = { userId: number; nick: string | null; bet: string; win: string; refund: string; net: string; days: number };
type Range = { from: string; to: string };

const shift = (d: string, n: number) => new Date(new Date(d + 'T00:00:00Z').getTime() + n * 86_400_000).toISOString().slice(0, 10);
const sum = <T,>(rows: T[], k: keyof T) => rows.reduce((a, r) => a + BigInt(r[k] as string), 0n);

function presets(today: string): [string, Range][] {
  return [
    ['Hôm nay', { from: today, to: today }],
    ['7 ngày', { from: shift(today, -6), to: today }],
    ['30 ngày', { from: shift(today, -29), to: today }],
    ['Tháng này', { from: today.slice(0, 8) + '01', to: today }],
  ];
}

function RangeBar({ value, onChange, exportPath, extra, children }: { value: Range; onChange: (r: Range) => void; exportPath: string; extra?: Record<string, string>; children?: ReactNode }) {
  const { can } = useAuth();
  const [draft, setDraft] = useState(value);
  // the day / week / month switch moves the range from outside: show it in the date boxes
  useEffect(() => { setDraft(value); }, [value.from, value.to]);
  const today = vnDayKey(new Date());
  const dl = useMutation({ mutationFn: () => api.download(exportPath, { ...value, ...extra, format: 'csv' }, 'bao-cao.csv') });
  const apply = (e: FormEvent) => { e.preventDefault(); onChange(draft); };
  return (
    <>
      <form className="toolbar-filters" onSubmit={apply}>
        {children}
        <TextInput id={exportPath + '-from'} type="date" labelText="Từ ngày" value={draft.from} max={draft.to} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
        <TextInput id={exportPath + '-to'} type="date" labelText="Đến ngày" value={draft.to} min={draft.from} max={today} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
        <Button type="submit" kind="tertiary" size="md" renderIcon={Filter}>Xem</Button>
        <div className="row presets">
          {presets(today).map(([l, r]) => (
            <Button key={l} kind={r.from === value.from && r.to === value.to ? 'secondary' : 'ghost'} size="sm" onClick={() => { setDraft(r); onChange(r); }}>{l}</Button>
          ))}
        </div>
        <span className="toolbar-filters__grow" />
        {can('data.export') ? <Button kind="ghost" size="md" renderIcon={Download} disabled={dl.isPending} onClick={() => dl.mutate()}>Xuất CSV</Button> : null}
      </form>
      {dl.error ? <InlineError error={dl.error} /> : null}
    </>
  );
}

function Totals({ rows, label, admin, rtp = false }: { rows: Row[]; label: string; admin: boolean; rtp?: boolean }) {
  const ggr = sum(rows, 'ggr'), bet = sum(rows, 'bet');
  return (
    <tr>
      <td className="is-title">{label}</td>
      <td className="r" data-label="Cược"><Money v={bet} /></td>
      <td className="r" data-label="Trả thưởng"><Money v={sum(rows, 'win')} /></td>
      <td className="r" data-label="Hoàn"><Money v={sum(rows, 'refund')} /></td>
      <td className="r" data-label="Lãi nhà cái"><Money v={ggr} signed /></td>
      <td className="r" data-label="Giữ lại"><Pct num={ggr} den={bet} /></td>
      {admin ? <td className="r" data-label="Ngoài game"><Money v={sum(rows, 'admin')} signed /></td> : null}
      <td />
      {rtp ? <td /> : null}
    </tr>
  );
}

const HEAD = (first: string, admin: boolean, rtp = false) => (
  <thead><tr>
    <th>{first}</th><th className="r">Cược</th><th className="r">Trả thưởng</th><th className="r">Hoàn</th>
    <th className="r">Lãi nhà cái</th><th className="r">Giữ lại</th>{admin ? <th className="r">Ngoài game</th> : null}<th className="r">Người chơi</th>
    {rtp ? <th className="r">RTP thực / lý thuyết</th> : null}
  </tr></thead>
);

const pct = (v: number | null) => (v === null ? '-' : (v * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 }) + '%');

// RTP thuc te so voi RTP ly thuyet; ngoai dai rtp +- z * sigma / sqrt(so lan cuoc) thi gan the
function RtpCell({ r }: { r: GameRow }) {
  const out = r.rtp !== null && r.rtpLow !== null && r.rtpHigh !== null && (r.rtp < r.rtpLow || r.rtp > r.rtpHigh);
  return (
    <td className="r num" data-label="RTP thực / lý thuyết"
      title={r.rtpLow !== null && r.rtpHigh !== null ? `Dải bình thường ${pct(r.rtpLow)} đến ${pct(r.rtpHigh)}` : undefined}>
      {pct(r.rtp)} <span className="muted">/ {pct(r.rtpTheory)}</span>
      {out ? <> <Tag type="red" size="sm">Ngoài dải</Tag></> : null}
    </td>
  );
}

function Cells({ r, admin }: { r: Row; admin: boolean }) {
  return <>
    <td className="r" data-label="Cược"><Money v={r.bet} /></td>
    <td className="r" data-label="Trả thưởng"><Money v={r.win} /></td>
    <td className="r" data-label="Hoàn"><Money v={r.refund} /></td>
    <td className="r" data-label="Lãi nhà cái"><Money v={r.ggr} signed /></td>
    <td className="r" data-label="Giữ lại"><Pct num={r.ggr} den={r.bet} /></td>
    {admin ? <td className="r" data-label="Ngoài game"><Money v={r.admin} signed /></td> : null}
    <td className="r num" data-label="Người chơi">{r.players}</td>
  </>;
}

// one row per day, week (Monday to Sunday) or month; the players of a week or month are counted once
function Daily() {
  const today = vnDayKey(new Date());
  const [grain, setGrain] = useState<Grain>('day');
  const [range, setRange] = useState<Range>({ from: shift(today, -29), to: today });
  const q = useQuery({ queryKey: ['reports', 'daily', range, grain], queryFn: () => api.get<{ rows: DayRow[] }>('/api/admin/reports/daily', { ...range, grain }) });
  const rows = q.data?.rows ?? [];
  const any = rows.some((r) => r.rows > 0);
  const pick = (g: Grain) => { setGrain(g); setRange({ from: periodDefaultFrom(range.to, g), to: range.to }); };
  const word = PERIOD_WORD[grain];
  return (
    <>
      <GrainSwitch value={grain} onChange={pick} />
      <RangeBar value={range} onChange={setRange} exportPath="/api/admin/reports/daily" extra={{ grain }} />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !any ? (
        <EmptyState title="Không có giao dịch game trong khoảng này" body="Chọn khoảng ngày khác." />
      ) : (
        <>
          <div className="panel"><BarChart title={`Lãi nhà cái theo ${word}`} bars={rows.map((r) => ({ key: r.day, label: periodTick(r.day, grain, r.partial), value: BigInt(r.ggr), hint: `${periodLabel(r.day, grain)}${r.partial && grain !== 'day' ? '*' : ''}, cược ${Number(BigInt(r.bet) / 1000n).toLocaleString('vi-VN')}k, ${r.players} người chơi` }))} /></div>
          <table className="plain section">
            {HEAD(GRAIN_LABEL[grain], true)}
            <tbody>{[...rows].reverse().map((r) => <tr key={r.day}><td className="num is-title">{periodLabel(r.day, grain)}{r.partial && grain !== 'day' ? '*' : ''}</td><Cells r={r} admin /></tr>)}</tbody>
            <tfoot><Totals rows={rows} label="Cộng" admin /></tfoot>
          </table>
          <p className="muted note">Người chơi = số tài khoản có đặt cược trong {word} đó, mỗi người đếm một lần.{grain !== 'day' ? ' ' + PARTIAL_NOTE : ''}</p>
        </>
      )}
    </>
  );
}

function Games() {
  const today = vnDayKey(new Date());
  const [range, setRange] = useState<Range>({ from: shift(today, -6), to: today });
  const q = useQuery({ queryKey: ['reports', 'games', range], queryFn: () => api.get<{ rows: GameRow[] }>('/api/admin/reports/games', range) });
  const rows = q.data?.rows ?? [];
  return (
    <>
      <RangeBar value={range} onChange={setRange} exportPath="/api/admin/reports/games" />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !rows.length ? (
        <EmptyState title="Không có game nào có giao dịch trong khoảng này" body="Chọn khoảng ngày khác." />
      ) : (
        <table className="plain">
          {HEAD('Game', false, true)}
          <tbody>{rows.map((r) => <tr key={r.game}><td className="is-title">{r.game}</td><Cells r={r} admin={false} /><RtpCell r={r} /></tr>)}</tbody>
          <tfoot><Totals rows={rows} label="Cộng" admin={false} rtp /></tfoot>
        </table>
      )}
      <p className="muted note">Người chơi là số tài khoản có đặt cược trong cả khoảng ngày; tài khoản thử và nhân viên không tính. RTP thực = trả thưởng / (cược - hoàn); gắn "Ngoài dải" khi lệch khỏi RTP lý thuyết quá mức ngẫu nhiên cho phép với số lần cược đó. Tiền cộng trừ tay, giftcode, chuyển khoản không thuộc game nào và không tính vào lãi; xem ở cột "Ngoài game" của báo cáo theo ngày.</p>
    </>
  );
}

// Lệnh hoà: rounds that ended in a draw and gave the stake back (a part of it, for Rồng Hổ). Not a win and not a loss:
// the house profit of the other tabs is untouched. Source and limits: services/reports/ties.ts.
function Ties() {
  const today = vnDayKey(new Date());
  const [grain, setGrain] = useState<Grain>('day');
  // /reports?tab=hoa&days=3 opens on the last 3 days
  const days = Math.min(366, Math.max(1, Number(new URLSearchParams(window.location.search).get('days')) || 30));
  const [range, setRange] = useState<Range>({ from: shift(today, 1 - days), to: today });
  const q = useQuery({ queryKey: ['reports', 'ties', range, grain], queryFn: () => api.get<TieData>('/api/admin/reports/ties', { ...range, grain }) });
  const rows = q.data?.rows ?? [];
  const games = q.data?.games ?? [];
  const pick = (g: Grain) => { setGrain(g); setRange({ from: periodDefaultFrom(range.to, g), to: range.to }); };
  const t = q.data?.totals;
  return (
    <>
      <GrainSwitch value={grain} onChange={pick} />
      <RangeBar value={range} onChange={setRange} exportPath="/api/admin/reports/ties" extra={{ grain }} />
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !t || (t.ties === 0 && BigInt(t.returned) === 0n) ? (
        <EmptyState title="Không có lệnh hoà trong khoảng này" body="Chọn khoảng ngày khác." />
      ) : (
        <>
          <table className="plain section">
            <thead><tr><th>{GRAIN_LABEL[grain]}</th><th className="r">Số lệnh hoà</th><th className="r">Tiền cược</th><th className="r">Đã hoàn</th><th className="r">Nhà giữ</th></tr></thead>
            <tbody>{[...rows].reverse().map((r) => (
              <tr key={r.day}>
                <td className="num is-title">{periodLabel(r.day, grain)}{r.partial && grain !== 'day' ? '*' : ''}</td>
                <td className="r num" data-label="Số lệnh hoà">{r.ties}</td>
                <td className="r" data-label="Tiền cược"><Money v={r.stake} /></td>
                <td className="r" data-label="Đã hoàn"><Money v={r.returned} /></td>
                <td className="r" data-label="Nhà giữ"><Money v={r.kept} /></td>
              </tr>))}</tbody>
            <tfoot><tr>
              <td className="is-title">Cộng</td>
              <td className="r num" data-label="Số lệnh hoà">{t.ties}</td>
              <td className="r" data-label="Tiền cược"><Money v={t.stake} /></td>
              <td className="r" data-label="Đã hoàn"><Money v={t.returned} /></td>
              <td className="r" data-label="Nhà giữ"><Money v={t.kept} /></td>
            </tr></tfoot>
          </table>
          <div className="section"><h2>Theo game</h2></div>
          <table className="plain">
            <thead><tr><th>Game</th><th className="r">Số lệnh hoà</th><th className="r">Tiền cược</th><th className="r">Đã hoàn</th><th className="r">Nhà giữ</th></tr></thead>
            <tbody>{games.map((r) => (
              <tr key={r.game}>
                <td className="is-title">{r.label}</td>
                <td className="r num" data-label="Số lệnh hoà">{r.ties}</td>
                <td className="r" data-label="Tiền cược"><Money v={r.stake} /></td>
                <td className="r" data-label="Đã hoàn"><Money v={r.returned} /></td>
                <td className="r" data-label="Nhà giữ"><Money v={r.kept} /></td>
              </tr>))}</tbody>
          </table>
        </>
      )}
      <p className="muted note">Lệnh hoà = một lần ván hoà trả lại tiền cược cho một người chơi (mỗi người một dòng mỗi ván; MegaPoker gộp các tay của một lượt quay). Đã hoàn là tiền thật đã trả lại; Nhà giữ là phần cược nhà cái giữ khi hoà (Rồng Hổ hoàn một phần, đọc từ vé cược; HiLo, MegaPoker, Aviator và Live casino coi như hoàn đủ nên bằng 0, kể cả khi cấu hình RTP làm tiền trả lại thấp hơn cược); Tiền cược = Đã hoàn + Nhà giữ. Hoà không phải thắng hay thua: lãi nhà cái ở các báo cáo khác không đổi.{grain !== 'day' ? ' ' + PARTIAL_NOTE : ''} Số liệu theo giờ {timeZoneLabel()}, ngày chốt ván. Tài khoản thử, nhân viên và bot nhà cái không tính.</p>
      {q.data ? (
        <>
          {q.data.unmatched > 0 ? <p className="muted note">{q.data.unmatched} lệnh Rồng Hổ chưa ghép được với vé cược của ván đó: Nhà giữ của các lệnh này tính bằng 0.</p> : null}
          <p className="muted note">Chưa có số liệu hoà cho: {q.data.notCovered.join('; ')}.</p>
        </>
      ) : null}
    </>
  );
}

function Players() {
  const { can } = useAuth();
  const today = vnDayKey(new Date());
  const [range, setRange] = useState<Range>({ from: shift(today, -6), to: today });
  const [order, setOrder] = useState('winners');
  const [game, setGame] = useState('');
  const params = { ...range, order, game };
  const q = useQuery({ queryKey: ['reports', 'players', params], queryFn: () => api.get<{ items: PlayerRow[]; games: string[] }>('/api/admin/reports/players', params) });
  const items = q.data?.items ?? [];
  return (
    <>
      <RangeBar value={range} onChange={setRange} exportPath="/api/admin/reports/players" extra={{ order, game }}>
        <Select id="rp-order" labelText="Xếp theo" value={order} onChange={(e) => setOrder(e.target.value)}>
          <SelectItem value="winners" text="Người thắng nhiều nhất" />
          <SelectItem value="losers" text="Người thua nhiều nhất" />
          <SelectItem value="volume" text="Cược nhiều nhất" />
        </Select>
        <Select id="rp-game" labelText="Game" value={game} onChange={(e) => setGame(e.target.value)}>
          <SelectItem value="" text="Tất cả" />
          {(q.data?.games ?? []).map((g) => <SelectItem key={g} value={g} text={g} />)}
        </Select>
      </RangeBar>
      {q.isLoading ? <BlockSkeleton lines={8} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : !items.length ? (
        <EmptyState title="Không có người chơi nào khớp" body="Đổi game hoặc khoảng ngày." />
      ) : (
        <table className="plain">
          <thead><tr><th className="r">#</th><th>Người chơi</th><th className="r">Cược</th><th className="r">Trả thưởng</th><th className="r">Hoàn</th><th className="r">Thắng thua</th><th className="r">Số ngày chơi</th></tr></thead>
          <tbody>
            {items.map((r, i) => (
              <tr key={r.userId}>
                <td className="r num muted" data-label="#">#{i + 1}</td>
                <td className="is-title">{can('users.view') ? <Link className="cell-link" to={`/users/${r.userId}`}>{r.nick ?? '#' + r.userId}</Link> : r.nick ?? '#' + r.userId}</td>
                <td className="r" data-label="Cược"><Money v={r.bet} /></td>
                <td className="r" data-label="Trả thưởng"><Money v={r.win} /></td>
                <td className="r" data-label="Hoàn"><Money v={r.refund} /></td>
                <td className="r" data-label="Thắng thua"><Money v={r.net} signed /></td>
                <td className="r num" data-label="Số ngày chơi">{r.days}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted note">Thắng thua tính từ phía người chơi: số dương là người chơi thắng nhà cái. Tối đa 100 người.</p>
    </>
  );
}

// /reports?tab=hoa opens a tab straight away (a link from another screen, a screenshot)
const TAB_KEYS = ['ngay', 'game', 'nguoi', 'hoa'];

export function ReportsPage() {
  const [tab, setTab] = useState(() => Math.max(0, TAB_KEYS.indexOf(new URLSearchParams(window.location.search).get('tab') ?? '')));
  return (
    <div className="page">
      <PageHeader title="Báo cáo" lead={`Tổng hợp theo ngày, ${timeZoneLabel()} từ sổ giao dịch, tiền tính bằng KRW (₩). Lãi nhà cái = cược - trả thưởng - hoàn cược.`} />
      <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
        <TabList aria-label="Loại báo cáo">
          <Tab>Theo ngày</Tab>
          <Tab>Theo game</Tab>
          <Tab>Người chơi</Tab>
          <Tab>Lệnh hoà</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>{tab === 0 ? <Daily /> : null}</TabPanel>
          <TabPanel>{tab === 1 ? <Games /> : null}</TabPanel>
          <TabPanel>{tab === 2 ? <Players /> : null}</TabPanel>
          <TabPanel>{tab === 3 ? <Ties /> : null}</TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
}
